import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SectionHeading } from "@/components/bespoke/section-heading";
import { useState } from "react";
import { Link } from "react-router";
import {
  BookOpen,
  CreditCard,
  GitBranch,
  Cpu,
  Database,
  Webhook,
  Shield,
  DollarSign,
} from "lucide-react";
import { PageHeader } from "@/components/bespoke";
import { NotConnectedState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ApiError,
  connectionActivityQuery,
  connectionsQuery,
  createConnection,
  paymentConnectionQuery,
  projectSettingsQuery,
  updateProjectSettings,
} from "@/lib/api";
import { checkDraft, defaultScopes, draftToRequest, providerFor } from "@/pages/add-connection";
import { canonicalIntegrationTypes } from "@zworks/console-contracts";
import { useCurrentProject } from "@/lib/project";
import { integrationGuideStatuses, integrationGuides } from "@/pages/integration-guides";
import { describeActivityAction, formatActivityTime } from "@/pages/connection-activity";
import {
  attentionStatuses,
  catalogueStatus,
  connectionOverview,
  type IntegrationTab,
} from "@/pages/integrations-overview";
import type { Connection } from "@zworks/console-contracts";

const paymentStatusLabels = {
  "not-configured": "Not connected yet",
  "test-mode": "Test mode",
  live: "Live",
  unavailable: "Unavailable",
} as const;

const healthLabels = {
  healthy: "Healthy",
  degraded: "Degraded",
  unreachable: "Unreachable",
  unknown: "Unknown",
} as const;

const healthVariants = {
  healthy: "success" as const,
  degraded: "warning" as const,
  unreachable: "destructive" as const,
  unknown: "secondary" as const,
};

const statusLabels = {
  pending: "Pending",
  active: "Active",
  degraded: "Degraded",
  failing: "Failing",
  expired: "Expired",
  revoked: "Revoked",
  blocked: "Blocked",
} as const;

const statusVariants = {
  pending: "warning" as const,
  active: "success" as const,
  degraded: "warning" as const,
  failing: "destructive" as const,
  expired: "secondary" as const,
  revoked: "secondary" as const,
  blocked: "destructive" as const,
};

const integrationTypeIcons: Record<string, React.ElementType> = {
  github: GitBranch,
  gitlab: GitBranch,
  anthropic: Cpu,
  openai: Cpu,
  postgres_mcp: Database,
  custom_mcp: Database,
  slack: Webhook,
  ms_teams: Webhook,
  jira: Shield,
  linear: Shield,
  npm_registry: Database,
  go_proxy: Database,
  crates_io: Database,
  webhook_outbound: Webhook,
  google_sso: Shield,
  generic_payment: DollarSign,
};

/** Renders a connection card showing status and health. */
function ConnectionCard({ connection, projectId }: { connection: Connection; projectId: string }) {
  const [activityOpen, setActivityOpen] = useState(false);
  const activity = useInfiniteQuery({
    ...connectionActivityQuery(projectId, connection.id),
    enabled: activityOpen,
  });
  const activityEntries = activity.data?.pages.flatMap((page) => page.activity) ?? [];
  const Icon = integrationTypeIcons[connection.integrationType] ?? Cpu;
  const health = connection.health as keyof typeof healthLabels;
  const status = connection.status as keyof typeof statusLabels;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon aria-hidden className="size-4 shrink-0" />
          <Link
            to={`/integrations/connections/${connection.id}`}
            className="underline-offset-4 hover:underline"
          >
            {connection.name}
          </Link>
        </CardTitle>
        <CardDescription className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground capitalize">
            {connection.integrationType}
          </span>
          <Badge variant={statusVariants[status] || "secondary"}>
            {statusLabels[status] || connection.status}
          </Badge>
          {connection.granted ? (
            <Badge variant="secondary" title="Shared with this project by its organisation">
              Organisation connection
            </Badge>
          ) : null}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-2">
        <p className="text-sm text-muted-foreground">
          Auth: {connection.authMethod} · Scopes:{" "}
          {connection.scopes.length > 0 ? connection.scopes.join(", ") : "none"}
        </p>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Health:</span>
          <Badge variant={healthVariants[health] || "secondary"}>
            {healthLabels[health] || connection.health}
          </Badge>
          {connection.healthCheckedAt && (
            <span className="text-xs text-muted-foreground">
              Last checked {new Date(connection.healthCheckedAt).toLocaleString()}
            </span>
          )}
        </div>
        {connection.expiresAt && (
          <p className="text-sm text-muted-foreground">
            Expires: {new Date(connection.expiresAt).toLocaleDateString()}
          </p>
        )}
        {connection.granted ? (
          <p className="text-xs text-muted-foreground">
            Granted to this project by its organisation. Other projects in the organisation cannot
            use it.
          </p>
        ) : null}
        <details
          className="border-t pt-3"
          onToggle={(event) => setActivityOpen(event.currentTarget.open)}
        >
          <summary className="cursor-pointer text-sm font-medium underline-offset-4 hover:underline">
            Activity
          </summary>
          <div className="pt-3" aria-live="polite">
            {activity.isPending ? (
              <p className="text-sm text-muted-foreground" role="status">
                Loading connection activity…
              </p>
            ) : activity.isError ? (
              <div className="flex flex-col items-start gap-2" role="alert">
                <p className="text-sm text-muted-foreground">
                  {activity.error instanceof ApiError
                    ? (activity.error.problem?.detail ?? activity.error.message)
                    : "Connection activity could not be loaded."}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void activity.refetch()}
                >
                  Try again
                </Button>
              </div>
            ) : activityEntries.length > 0 ? (
              <>
                <ol className="divide-y" aria-label={`Activity for ${connection.name}`}>
                  {activityEntries.map((entry) => (
                    <li key={entry.id} className="flex flex-col gap-1 py-2 first:pt-0 last:pb-0">
                      <span className="text-sm font-medium">
                        {describeActivityAction(entry.action)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {entry.actorKind === "system" ? "System" : "Member"} ·{" "}
                        <time dateTime={entry.occurredAt}>
                          {formatActivityTime(entry.occurredAt)}
                        </time>
                      </span>
                    </li>
                  ))}
                </ol>
                {activity.hasNextPage && (
                  <div className="flex flex-col items-start gap-2 pt-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={activity.isFetchingNextPage}
                      onClick={() => void activity.fetchNextPage()}
                    >
                      Show older
                    </Button>
                    {activity.isFetchNextPageError && (
                      <p className="text-sm text-muted-foreground" role="alert">
                        Older activity could not be loaded. Try again.
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No connection activity recorded yet.</p>
            )}
          </div>
        </details>
      </CardContent>
    </Card>
  );
}

/** The payment provider connection card, with status and probe health. */
function PaymentConnectionCard({ projectId }: { projectId: string }) {
  const payment = useQuery({ ...paymentConnectionQuery(projectId), enabled: projectId.length > 0 });
  const state = payment.data;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard aria-hidden className="size-4 shrink-0" />
          Payments
        </CardTitle>
        <CardDescription>
          Billing ·{" "}
          <Badge variant={state?.status === "live" ? "success" : "unknown"}>
            {state ? paymentStatusLabels[state.status] : "Loading"}
          </Badge>
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-2">
        <p className="flex-1 text-sm text-muted-foreground">
          {state?.testMode
            ? "Test-mode checkout is on for this project. No money can move; live stays off until a provider is wired."
            : "Card checkout, invoices, and credit pack billing. Live checkout stays off until a payment provider is wired."}
        </p>
        <p className="text-sm text-muted-foreground">
          Health:{" "}
          <Badge variant={state?.health === "healthy" ? "success" : "muted"}>
            {state?.health ?? "unknown"}
          </Badge>
        </p>
      </CardContent>
    </Card>
  );
}

/** The overview strip: three figures and no chart, because each answers a question on its own. */
function OverviewStrip({ connections }: { connections: readonly Connection[] }) {
  const overview = connectionOverview(connections);
  const figures = [
    { label: "Connected", value: overview.connected },
    { label: "Need attention", value: overview.needsAttention },
    { label: "Credentials", value: overview.credentials },
  ];
  return (
    <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
      {figures.map((figure) => (
        <div key={figure.label} className="rounded-md border p-4">
          <dt className="text-sm text-muted-foreground">{figure.label}</dt>
          <dd className="text-2xl font-semibold tabular-nums">{figure.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The Available tab: the setup guides, each labelled with whether it can be connected today. */
function AvailableTab({ connections }: { connections: readonly Connection[] }) {
  return (
    <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {integrationGuides.map((guide) => {
        const status = integrationGuideStatuses[guide.status];
        const Icon = guide.icon;
        const live = catalogueStatus(guide.slug, connections) === "connected";
        return (
          <li key={guide.title}>
            <Card className="flex h-full flex-col">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Icon aria-hidden className="size-4 shrink-0" />
                  {guide.title}
                </CardTitle>
                {/* Colour never carries the status alone: the badge always has a label. */}
                <CardDescription>
                  {guide.category} ·{" "}
                  <Badge variant={live ? "success" : status.variant}>
                    {live ? "Connected" : status.label}
                  </Badge>
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col gap-4">
                <p className="flex-1 text-sm text-muted-foreground">{guide.summary}</p>
                <a
                  className="inline-flex items-center gap-2 text-sm font-medium text-accent-text underline-offset-4 hover:underline"
                  href={guide.guidePath}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Read the ${guide.title} setup guide`}
                >
                  <BookOpen aria-hidden className="size-4" />
                  Read the setup guide
                </a>
              </CardContent>
            </Card>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The add-connection form. The provider decides which auth methods and scopes are offered, so a
 * person cannot ask for one the provider never grants.
 */
function AddConnectionForm({ projectId, onAdded }: { projectId: string; onAdded: () => void }) {
  const [integrationType, setIntegrationType] = useState("");
  const [name, setName] = useState("");
  const [authMethod, setAuthMethod] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const provider = providerFor(integrationType);

  const create = useMutation({
    mutationFn: (key: string) => {
      const request = draftToRequest({ integrationType, name, authMethod }, key);
      if (request === null) {
        throw new Error("The form is incomplete.");
      }
      return createConnection(projectId, request);
    },
    onSuccess: () => {
      setFeedback("Connection created. It is listed on the Connected tab.");
      setError(null);
      setIntegrationType("");
      setName("");
      setAuthMethod("");
      onAdded();
    },
    onError: (caught) => {
      setFeedback(null);
      setError(
        caught instanceof ApiError
          ? (caught.problem?.detail ?? caught.message)
          : "The connection could not be created.",
      );
    },
  });

  const problem = checkDraft({ integrationType, name, authMethod });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add a connection</CardTitle>
        <CardDescription>
          A connection starts with the provider&rsquo;s read scopes. Write scopes are granted
          afterwards, through the approval the Policies tab describes.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (problem === null) {
              // A fresh key per attempt: a retried submit reuses the row instead of creating a
              // second connection.
              create.mutate(crypto.randomUUID());
            }
          }}
        >
          <label className="flex flex-col gap-1 text-sm">
            Provider
            <select
              className="h-11 rounded-md border bg-background px-3 md:h-9"
              value={integrationType}
              onChange={(event) => {
                setIntegrationType(event.target.value);
                setAuthMethod("");
              }}
            >
              <option value="">Choose a provider</option>
              {canonicalIntegrationTypes.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-sm">
            Name
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Team GitHub"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            Authentication
            <select
              className="h-11 rounded-md border bg-background px-3 md:h-9"
              value={authMethod}
              onChange={(event) => setAuthMethod(event.target.value)}
              disabled={provider === undefined}
            >
              <option value="">Choose a method</option>
              {(provider?.authMethods ?? []).map((method) => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </select>
          </label>

          {provider !== undefined ? (
            <p className="text-xs text-muted-foreground">
              Requested scopes: {defaultScopes(provider).join(", ")}
            </p>
          ) : null}

          <div className="flex flex-col items-start gap-2">
            <Button type="submit" disabled={problem !== null || create.isPending}>
              Add connection
            </Button>
            {problem !== null ? <p className="text-xs text-muted-foreground">{problem}</p> : null}
            {feedback !== null ? (
              <p className="text-xs text-muted-foreground" role="status">
                {feedback}
              </p>
            ) : null}
            {error !== null ? (
              <p className="text-xs" role="alert">
                {error}
              </p>
            ) : null}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/** The Policies tab. Every line states where the platform actually stands, not where it aims to be. */
function PoliciesTab({ projectId }: { projectId: string }) {
  const client = useQueryClient();
  const settings = useQuery(projectSettingsQuery(projectId));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const toggle = useMutation({
    mutationFn: (required: boolean) =>
      updateProjectSettings(projectId, {
        // The endpoint replaces the whole record, so every field it requires is sent. Reading
        // them from the query rather than from defaults is what keeps this from resetting a
        // setting somebody else changed.
        name: settings.data!.name,
        description: settings.data!.description,
        timeZone: settings.data!.timeZone,
        defaultModelTier: settings.data!.defaultModelTier,
        defaultEffort: settings.data!.defaultEffort,
        perRunCap: settings.data!.perRunCap,
        concurrentRunLimit: settings.data!.concurrentRunLimit,
        scopeApprovalRequired: required,
      }),
    onSuccess: async (updated) => {
      setError(null);
      setSaved(
        updated.scopeApprovalRequired
          ? "A widened scope set now needs a second person's approval."
          : "A widened scope set now applies without approval.",
      );
      await client.invalidateQueries(projectSettingsQuery(projectId));
    },
    onError: (caught) => {
      setSaved(null);
      setError(
        caught instanceof ApiError
          ? (caught.problem?.detail ?? caught.message)
          : "The policy could not be changed.",
      );
    },
  });

  const required = settings.data?.scopeApprovalRequired ?? false;
  const editable = settings.isSuccess;

  const staticPolicies = [
    {
      title: "Who can connect",
      detail:
        "The project owner and developers can add a connection or change its scopes. Other members can read them.",
    },
    {
      title: "Signed webhooks",
      detail:
        "Outbound deliveries are signed with a secret of at least 32 bytes, and the signature covers the timestamp and the body.",
    },
    {
      title: "Network allowlist",
      detail:
        "Per-host allowlisting is the design target, not a fact yet: the proxy still permits any public host. Tracking this is separate work.",
    },
    {
      title: "Rotation reminders",
      detail:
        "A credential nearing its expiry is surfaced on the connection card before it lapses.",
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield aria-hidden className="size-4 shrink-0" />
            Approval for new scopes
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            A widened scope set needs a second person&rsquo;s approval. The author of a change never
            approves it.
          </p>
          {settings.isPending ? (
            <p className="text-sm text-muted-foreground" role="status">
              Loading the policy&hellip;
            </p>
          ) : settings.isError ? (
            <div className="flex flex-col items-start gap-2" role="alert">
              <p className="text-sm text-muted-foreground">
                {settings.error instanceof ApiError
                  ? (settings.error.problem?.detail ?? settings.error.message)
                  : "The policy could not be loaded."}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void settings.refetch()}
              >
                Try again
              </Button>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant={required ? "success" : "secondary"}>
                  {required ? "Required" : "Not required"}
                </Badge>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!editable || toggle.isPending}
                  onClick={() => toggle.mutate(!required)}
                >
                  {required ? "Turn approval off" : "Require approval"}
                </Button>
              </div>
              {saved !== null ? (
                <p className="text-xs text-muted-foreground" role="status">
                  {saved}
                </p>
              ) : null}
              {error !== null ? (
                <p className="text-xs" role="alert">
                  {error}
                </p>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      <ul className="grid gap-4 md:grid-cols-2">
        {staticPolicies.map((policy) => (
          <li key={policy.title}>
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield aria-hidden className="size-4 shrink-0" />
                  {policy.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{policy.detail}</p>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The catalogue of external systems this project can connect to, with setup guide links. */
export function IntegrationsPage() {
  const { project, loaded } = useCurrentProject();
  const [tab, setTab] = useState<IntegrationTab>("overview");
  const connections = useQuery({
    ...connectionsQuery(project?.id ?? ""),
    enabled: loaded && !!project,
  });
  const overview = connectionOverview(connections.data ?? []);
  const live = connections.data ?? [];
  const loading = connections.isLoading;
  const failed = connections.isError;
  // A count derived from a list that never arrived is a claim, not a number. Until the query
  // settles, the strip shows a placeholder and the sections say they are loading rather than "none".
  const known = !loading && !failed;
  const attention = live.filter(
    (conn) => attentionStatuses.has(conn.status) || conn.health === "unreachable",
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Integrations" />
      {loaded && project ? (
        <>
          {failed ? (
            <div className="flex flex-col items-start gap-2" role="alert">
              <p className="text-sm text-muted-foreground">
                {connections.error instanceof ApiError
                  ? (connections.error.problem?.detail ?? connections.error.message)
                  : "Connections could not be loaded."}
              </p>
              {/* Every other route's error names a reference the support log can be matched
                  against. This one did not, so a reader could quote the message and nothing else.
                  Measured by forcing a 500: no "Reference" and no id anywhere on the page. */}
              {connections.error instanceof ApiError ? (
                <p className="text-xs text-muted-foreground">
                  Reference{" "}
                  <span className="font-mono text-foreground">
                    {connections.error.requestId ?? "unavailable"}
                  </span>
                </p>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void connections.refetch()}
              >
                Try again
              </Button>
            </div>
          ) : loading ? (
            <p className="text-sm text-muted-foreground" role="status">
              Loading connections…
            </p>
          ) : (
            <OverviewStrip connections={live} />
          )}

          <Tabs value={tab} onValueChange={(value) => setTab(value as IntegrationTab)}>
            <TabsList aria-label="Integrations sections">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="connected">
                Connected{known ? ` (${overview.connected})` : ""}
              </TabsTrigger>
              <TabsTrigger value="available">Available</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="policies">Policies</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              {!known ? (
                <p className="text-sm text-muted-foreground" role="status">
                  {loading ? "Loading connections…" : "Connections could not be loaded."}
                </p>
              ) : attention.length > 0 ? (
                // The overview answers "what needs me": only the connections that are failing,
                // blocked, expired, or unreachable. The full list is the Connected tab.
                <div className="flex flex-col gap-3">
                  <h3 className="text-sm font-medium">Needing attention</h3>
                  <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {attention.map((conn) => (
                      <li key={conn.id}>
                        <ConnectionCard connection={conn} projectId={project.id} />
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <NotConnectedState
                  title={live.length > 0 ? "Everything is healthy" : "No connections yet"}
                  description={
                    live.length > 0
                      ? "No connection is failing, blocked, expired, or unreachable. The Connected tab lists them all."
                      : "Nothing is connected for this project. The Available tab lists what can be connected today, and the guides describe the setup. Credentials stay sealed and are never shown."
                  }
                />
              )}
            </TabsContent>

            <TabsContent value="connected">
              <h3 id="connected-heading" tabIndex={-1} className="mb-3 text-sm font-medium">
                Connected
              </h3>
              {!known ? (
                <p className="text-sm text-muted-foreground" role="status">
                  {loading ? "Loading connections…" : "Connections could not be loaded."}
                </p>
              ) : live.length > 0 ? (
                <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {live.map((conn) => (
                    <li key={conn.id}>
                      <ConnectionCard connection={conn} projectId={project.id} />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No connection exists for this project yet.
                </p>
              )}
            </TabsContent>

            <TabsContent value="available">
              <div className="flex flex-col gap-6">
                {project ? (
                  <AddConnectionForm
                    projectId={project.id}
                    onAdded={() => {
                      void connections.refetch();
                      setTab("connected");
                    }}
                  />
                ) : null}
                <AvailableTab connections={live} />
              </div>
            </TabsContent>

            <TabsContent value="activity">
              {/* The activity log is per connection today, because that is where the API records it.
                  A project-wide log needs an endpoint that does not exist yet, and inventing one here
                  would show an empty list that looks like "nothing happened". */}
              {live.length > 0 ? (
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-muted-foreground">
                    Activity is recorded per connection. Open a connection on the Connected tab to
                    read it.
                  </p>
                  <ul className="divide-y rounded-md border">
                    {live.map((conn) => (
                      <li key={conn.id} className="flex items-center justify-between gap-3 p-3">
                        <span className="text-sm font-medium">{conn.name}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setTab("connected")}
                        >
                          Open connection
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No connection activity recorded yet.
                </p>
              )}
            </TabsContent>

            <TabsContent value="policies">
              <PoliciesTab projectId={project.id} />
            </TabsContent>
          </Tabs>

          <section aria-label="Payment connection" className="flex flex-col gap-3">
            <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <li>
                <PaymentConnectionCard projectId={project.id} />
              </li>
            </ul>
          </section>
        </>
      ) : (
        <>
          <NotConnectedState
            title="Integrations are not connected yet"
            description="No connection exists for this project, and connection management is not available in this UI yet. The guides below describe the setup; credentials stay sealed and are never shown."
          />
          <section aria-labelledby="setup-guides-heading" className="flex flex-col gap-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <SectionHeading id="setup-guides-heading">Setup guides</SectionHeading>
              <span className="text-sm text-muted-foreground">
                {integrationGuides.length} guides
              </span>
            </div>
            <AvailableTab connections={[]} />
          </section>
        </>
      )}
    </div>
  );
}
